import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Table, {
	buttonsStyle,
} from "../tables/table";
import {
  getParticipantsByRegatta,
  postParticipant,
  deleteParticipant,
  updateParticipant 
} from "../../../api/participant";
import { alertError, alertSuccess } from "../../../utils/logger";
import SeleccionarSailorDialog from "./SeleccionarSailorDialog";
import ConfirmDialog from "./ConfirmDialog";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import EditIcon from "@material-ui/icons/Edit";

export default function ParticipantsModal({ show, onHide, regattaId, onParticipantsChange }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [participantToEdit, setParticipantToEdit] = useState(null);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (regattaId) {
      setLoading(true);
      getParticipantsByRegatta(regattaId)
        .then((res) => {
            if (res.status === 200) {
            console.log("📦 PARTICIPANTS FROM BACKEND:", res.data);
            setParticipants(res.data);
            } else {
            console.warn("❌ Unexpected status:", res.status);
            }
        })
        .catch((err) =>
          alertError({
            error: err,
            customMessage: "Could not load participants",
          })
        )
        .finally(() => setLoading(false));
    }
  }, [regattaId]);
  
  useEffect(() => {
    if (refresh) {
        getParticipantsByRegatta(regattaId)
        .then((res) => {
            if (res.status === 200) {
                setParticipants(res.data);
                if (onParticipantsChange) {
                    onParticipantsChange(regattaId, res.data.length);
                }
            }
        })
        .catch((err) =>
            alertError({
            error: err,
            customMessage: "Could not refresh participants",
            })
        )
        .finally(() => {
            setRefresh(false);
            setOpenConfirmDialog(false);
            setParticipantToDelete(null);
        });
    }
  }, [refresh, regattaId]);

  const handleAddParticipant = async ({ sailor, boat, gpsDevice }) => {
    try {
      await postParticipant({
        sailor,
        boat,
        regatta: regattaId,
        gpsDevice,
      });
      const updated = await getParticipantsByRegatta(regattaId);
      setParticipants(updated.data);
      if (onParticipantsChange) {
        onParticipantsChange(regattaId, updated.data.length);
      }
    } catch (error) {
      console.error("Error al crear participante:", error);
    } finally {
      setShowNewDialog(false);
    }
  };
  const handleEditParticipant = async ({ sailor, boat, gpsDevice }) => {
    if (!participantToEdit?._id) return;
    try {
      await updateParticipant(participantToEdit._id, {
        sailor,
        boat,
        gpsDevice,
        regatta: regattaId,
      });
      const updated = await getParticipantsByRegatta(regattaId);
      setParticipants(updated.data);
      if (onParticipantsChange) onParticipantsChange(regattaId, updated.data.length);
      alertSuccess({ title: "Updated", customMessage: "Participant updated successfully." });
    } catch (error) {
      alertError({ error, customMessage: "Could not update participant" });
    } finally {
      setShowEditDialog(false);
      setParticipantToEdit(null);
    }
  };

  const columns = [
    {
        dataField: "sailor.fullName",
        text: "Name",
        formatter: (value) => value || "-",
    },
    {
        dataField: "sailor.mail",
        text: "Email",
        formatter: (value) => value || "-",
    },
    {
        dataField: "boat.sailNumber",
        text: "Sail No.",
        formatter: (value, row) => row.boat?.sailNumber || "-",
    },
    {
        dataField: "gpsDevice",
        text: "GPS Device",
        formatter: (device) => device?.code || "-",
    },
    {
        text: "Actions",
        formatter: (cell, row) => (
            <>
              <IconButton
                size="small"
                onClick={() => {
                  setParticipantToEdit(row);
                  setShowEditDialog(true);
                }}
                style={buttonsStyle}
                title="Edit"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setParticipantToDelete(row);
                  setOpenConfirmDialog(true);
                }}
                style={buttonsStyle}
                title="Delete"
              >
                <DeleteIcon />
              </IconButton>
            </>
        ),
        align: "center",
    }
  ];

  const reservedForEdit = participants
    .filter(p => p._id !== participantToEdit?._id)
    .map(p => p?.gpsDevice?._id)
    .filter(Boolean);

  return (
    <>
      <Dialog
        open={show}
        onClose={onHide}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle disableTypography>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6">Participants ({participants.length})</Typography>
            <Box display="flex" alignItems="center" mt={1} className="box-copy-link">
                <Typography variant="body2" color="textSecondary">
                    Registration link:&nbsp;
                    <a
                    href={`https://sailradar.com/${regattaId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    >
                    sailradar.com/{regattaId}
                    </a>
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => {
                    navigator.clipboard.writeText(`https://sailradar.com/${regattaId}`);
                    alertSuccess({
                        title: "Link copied",
                        customMessage: "URL copied to clipboard",
                    });
                    }}
                    style={{ marginLeft: 4 }}
                >
                    <FileCopyIcon style={{ fontSize: 28, color: '#1b3a94' }} />
                </IconButton>
            </Box>

            <Box>
              <Button
                onClick={() => setShowNewDialog(true)}
                variant="contained"
                className="btn btn-primary"
              >
                Add new
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Table
              keyField="_id"
              data={participants}
              columns={columns}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onHide} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <SeleccionarSailorDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onSelect={handleAddParticipant}
        existingParticipants={participants}
        reservedGpsIds={participants
          .map(p => p?.gpsDevice?._id)
          .filter(Boolean)}
      />
      
      <SeleccionarSailorDialog
        open={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setParticipantToEdit(null);
        }}
        onSelect={handleEditParticipant}
        existingParticipants={participants.filter(p => p._id !== participantToEdit?._id)}
        initialValues={{
          sailor: participantToEdit?.sailor || null,
          boat: participantToEdit?.boat || null,
          gpsDevice: participantToEdit?.gpsDevice || null,
        }}
        mode="edit"
        reservedGpsIds={reservedForEdit}
      />
      <ConfirmDialog
        title={'Are you sure you want to remove this participant?'}
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
            deleteParticipant(participantToDelete._id)
                .then((res) => {
                    if (res.status === 204) {
                        alertSuccess({
                            title: 'Deleted!',
                            customMessage: 'Participant removed successfully.',
                        });
                        setRefresh(true);
                    }
                })
                .catch((error) => {
                    alertError({
                        error: error,
                        customMessage: 'Could not remove participant.',
                    });
                });
        }}
    />


    </>
  );
}
